<script setup>
// O aviso que faltava entre dois operadores da mesma conta.
//
// O histórico de disparo é da conta, não de quem disparou: se o Andrey mandou o
// template para o cliente 1 hoje de manhã, a lista da Aline à tarde chega aqui antes
// de sair. Cada linha nomeia o NÚMERO — é o que identifica o cliente para os dois
// lados — e diz quem disparou, a que horas e com qual template, porque "já foi
// disparado" sem essas três informações não deixa ninguém decidir nada.
//
// As saídas são três de propósito. Cancelar volta para a lista; remover tira só os
// repetidos e mantém o resto do lote; disparar mesmo assim existe porque às vezes o
// segundo template do dia é intencional — mas exige marcar a confirmação, e o
// servidor só aceita com o `acknowledge_duplicates` que ela liga.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/ui/Dialog.vue';
import DialogHeader from 'dashboard/components-next/ui/DialogHeader.vue';
import DialogTitle from 'dashboard/components-next/ui/DialogTitle.vue';
import Button from 'dashboard/components-next/ui/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  duplicates: { type: Array, default: () => [] },
  totalContacts: { type: Number, default: 0 },
  sending: { type: Boolean, default: false },
});

const emit = defineEmits(['update:open', 'remove', 'force']);

const { t } = useI18n();

const acknowledged = ref(false);

// Toda abertura começa desmarcada: herdar o "disparar mesmo assim" de um lote
// anterior transformaria o segundo alerta em formalidade.
watch(
  () => props.open,
  isOpen => {
    if (isOpen) acknowledged.value = false;
  }
);

const duplicateCount = computed(() => props.duplicates.length);
// Quantos sobram se o operador tirar os repetidos — é a pergunta que ele faz antes
// de clicar em remover, e sem ela o botão parece que cancela o disparo inteiro.
const remainingCount = computed(() =>
  Math.max(props.totalContacts - duplicateCount.value, 0)
);

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogHeader>
      <DialogTitle>
        {{ t('OUTBOUNDS.DUPLICATES.TITLE', { count: duplicateCount }) }}
      </DialogTitle>
    </DialogHeader>

    <div
      class="flex items-start gap-2 px-3 py-2 mt-4 border rounded-lg border-n-ruby-6 bg-n-ruby-2"
    >
      <i
        class="i-lucide-alert-triangle size-4 mt-0.5 shrink-0 text-n-ruby-11"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('OUTBOUNDS.DUPLICATES.EXPLANATION') }}
      </p>
    </div>

    <ul class="mt-3 overflow-y-auto divide-y max-h-64 divide-n-weak">
      <li
        v-for="duplicate in duplicates"
        :key="duplicate.phone_key"
        class="py-2"
      >
        <div class="flex items-baseline justify-between gap-3">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ duplicate.name || t('OUTBOUNDS.DUPLICATES.NO_NAME') }}
          </span>
          <span class="text-sm tabular-nums shrink-0 text-n-slate-12">
            {{ duplicate.phone }}
          </span>
        </div>
        <p class="mt-0.5 text-xs text-n-slate-10">
          {{
            t('OUTBOUNDS.DUPLICATES.LAST_DISPATCH', {
              user:
                duplicate.last_dispatch.user_name ||
                t('OUTBOUNDS.DUPLICATES.UNKNOWN_USER'),
              time: formatTime(duplicate.last_dispatch.dispatched_at),
              template: duplicate.last_dispatch.template_name,
            })
          }}
        </p>
        <p v-if="duplicate.dispatch_count > 1" class="text-xs text-n-ruby-11">
          {{
            t('OUTBOUNDS.DUPLICATES.TIMES_TODAY', {
              count: duplicate.dispatch_count,
            })
          }}
        </p>
      </li>
    </ul>

    <label
      class="flex items-start gap-2 px-3 py-3 mt-3 text-sm border rounded-lg cursor-pointer select-none border-n-weak bg-n-alpha-1 text-n-slate-12"
    >
      <Checkbox
        v-model="acknowledged"
        :disabled="sending"
        class="mt-0.5 shrink-0"
      />
      <span>{{ t('OUTBOUNDS.DUPLICATES.ACKNOWLEDGE') }}</span>
    </label>

    <div class="flex flex-wrap justify-end gap-2 mt-5">
      <Button
        variant="ghost"
        :disabled="sending"
        @click="emit('update:open', false)"
      >
        {{ t('OUTBOUNDS.CANCEL') }}
      </Button>
      <!-- Remover é a ação primária: o caminho seguro é o padrão, e disparar de novo
           é a exceção que precisa de um clique a mais na confirmação. -->
      <Button :disabled="sending" @click="emit('remove')">
        {{
          t('OUTBOUNDS.DUPLICATES.REMOVE', {
            count: duplicateCount,
            remaining: remainingCount,
          })
        }}
      </Button>
      <Button
        variant="destructive"
        :disabled="sending || !acknowledged"
        @click="emit('force')"
      >
        <i v-if="sending" class="mr-1 i-lucide-loader-2 size-4 animate-spin" />
        {{ t('OUTBOUNDS.DUPLICATES.FORCE') }}
      </Button>
    </div>
  </Dialog>
</template>
